import React, { useState } from 'react'
import { Button, Icon } from 'antd'

import { ImagePreview } from '../../../../../../../../components'
import {
  getEntityApiId,
  getEntityDescription,
  getEntityKey,
  isEntityVerified
} from '../../utils'
import './EntityListPreview.scss'

const getMappedAssets = (assets, entity) => {
  if (!Array.isArray(assets) || !entity) return []
  const entityKeys = [
    entity?.id,
    entity?.entity_id,
    entity?.entityId,
    entity?.character_key,
    entity?.outfit_key,
    entity?.location_key,
    entity?.placeholder
  ]
    .filter(Boolean)
    .map(value => String(value).toLowerCase().trim())

  const entityName = String(entity.name || entity.placeholder || '').toLowerCase().trim()

  return assets.filter(asset => {
    // 1. Match by parent ID
    const parentId = String(
      asset?.parent_entity_id ||
      asset?.parentEntityId ||
      asset?.entity_id ||
      asset?.entityId ||
      ''
    ).toLowerCase().trim()

    if (parentId && entityKeys.includes(parentId)) return true

    // 2. Match by direct names
    const assetName = String(
      asset?.parent_entity_name ||
      asset?.parentEntityName ||
      asset?.entity_name ||
      asset?.entityName ||
      asset?.character_name ||
      asset?.characterName ||
      asset?.name ||
      ''
    ).toLowerCase().trim()

    if (assetName && assetName === entityName) return true

    // 3. Match by tags
    const tags = Array.isArray(asset.tags)
      ? asset.tags
      : typeof asset.tags === 'string'
      ? asset.tags.split(',').map(t => t.trim())
      : []

    if (tags.some(tag => tag.toLowerCase().trim() === entityName)) return true

    return false
  })
}

const EntityListPreview = ({
  entities,
  phase,
  isActivePhase,
  phaseBusyAction,
  onVerifyEntity,
  showFullDescriptions = false,
  generatedAssets = [],
  referenceAssets = []
}) => {
  const [expandedEntityKeys, setExpandedEntityKeys] = useState({})

  if (!entities.length) return null

  const toggleDescription = entityKey => {
    setExpandedEntityKeys(prev => ({
      ...prev,
      [entityKey]: !prev[entityKey]
    }))
  }

  const getPreviewImages = assets =>
    assets
      .map(item => {
        const url = item.image_url || item.imageUrl || item.thumbnail_url
        if (!url) return null
        return {
          src: url,
          alt: item.name || 'Preview'
        }
      })
      .filter(Boolean)

  return (
    <div
      className={`creative-direction-chat__entity-list${
        showFullDescriptions
          ? ' creative-direction-chat__entity-list--drawer'
          : ''
      }`}
    >
      {entities.map((entity, index) => {
        const entityKey = getEntityKey(entity, index)
        const description = getEntityDescription(entity)
        const isExpanded =
          showFullDescriptions || Boolean(expandedEntityKeys[entityKey])
        const canExpand = !showFullDescriptions && description.length > 160
        const entityApiId = getEntityApiId(entity)
        const isVerified = isEntityVerified(entity)
        const isVerifying = phaseBusyAction === `verify:${phase}:${entityApiId}`

        // Mapped assets
        const mappedReferences = getMappedAssets(referenceAssets, entity)
        const mappedGenerated = getMappedAssets(generatedAssets, entity)
        const referencePreviewImages = getPreviewImages(mappedReferences)
        const generatedPreviewImages = getPreviewImages(mappedGenerated)

        return (
          <div className="creative-direction-chat__entity-item" key={entityKey}>
            <div className="creative-direction-chat__entity-item-main">
              <div className="creative-direction-chat__entity-item-header">
                <strong>
                  {entity.name || entity.placeholder || `Item ${index + 1}`}
                </strong>
                {isVerified ? (
                  <span className="creative-direction-chat__entity-status creative-direction-chat__entity-status--verified">
                    <Icon type="check-circle" />
                    Verified
                  </span>
                ) : isActivePhase ? (
                  <Button
                    size="small"
                    icon="check-circle"
                    loading={isVerifying}
                    disabled={
                      !entityApiId || Boolean(phaseBusyAction) || !onVerifyEntity
                    }
                    onClick={() => onVerifyEntity(phase, entity)}
                  >
                    Verify
                  </Button>
                ) : (
                  <span className="creative-direction-chat__entity-status">
                    <Icon type="clock-circle" />
                    Waiting
                  </span>
                )}
              </div>
              <span
                className={`creative-direction-chat__entity-description${
                  isExpanded
                    ? ' creative-direction-chat__entity-description--expanded'
                    : ''
                }`}
              >
                {description}
              </span>
              {canExpand ? (
                <button
                  type="button"
                  className="creative-direction-chat__entity-read-more"
                  aria-expanded={isExpanded}
                  onClick={() => toggleDescription(entityKey)}
                >
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              ) : null}

              {/* Mapped visuals and references underneath entity card inside the drawer */}
              {showFullDescriptions && (mappedReferences.length > 0 || mappedGenerated.length > 0) ? (
                <div className="creative-direction-chat__entity-mapped-assets">
                  {mappedReferences.length > 0 ? (
                    <div className="creative-direction-chat__entity-mapped-col">
                      <span className="creative-direction-chat__entity-mapped-subtitle">
                        Uploaded References
                      </span>
                      <div className="creative-direction-chat__entity-mapped-grid">
                        {mappedReferences.map((asset, assetIndex) => {
                          const imageUrl = asset.image_url || asset.imageUrl || asset.thumbnail_url
                          const initialIndex = referencePreviewImages.findIndex(img => img.src === imageUrl)
                          return (
                            <div className="creative-direction-chat__entity-mapped-tile" key={assetIndex}>
                              <ImagePreview
                                src={imageUrl}
                                alt="Reference"
                                images={referencePreviewImages}
                                initialIndex={initialIndex >= 0 ? initialIndex : 0}
                                cancelOnUnmount
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}

                  {mappedGenerated.length > 0 ? (
                    <div className="creative-direction-chat__entity-mapped-col">
                      <span className="creative-direction-chat__entity-mapped-subtitle">
                        Generated Visuals
                      </span>
                      <div className="creative-direction-chat__entity-mapped-grid">
                        {mappedGenerated.map((asset, assetIndex) => {
                          const imageUrl = asset.image_url || asset.imageUrl || asset.thumbnail_url
                          const initialIndex = generatedPreviewImages.findIndex(img => img.src === imageUrl)
                          return (
                            <div className="creative-direction-chat__entity-mapped-tile" key={assetIndex}>
                              <ImagePreview
                                src={imageUrl}
                                alt="Visual"
                                images={generatedPreviewImages}
                                initialIndex={initialIndex >= 0 ? initialIndex : 0}
                                cancelOnUnmount
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default EntityListPreview
